import { useTrackItemList } from '@/hooks/useTrackItemList';
import type { Product } from '@/types';
import { toGa4Item } from '@/utils/toGa4Item';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

interface SplitLayoutFeaturedKeyProductData {
  product: Product;
  listName?: string;
  /** GA4 item_list_id - Optimizely ContentLink.ID. */
  contentId?: number;
}

export const SplitLayoutFeaturedKeyProduct = ({
  data,
}: {
  data: SplitLayoutFeaturedKeyProductData;
}) => {
  const { product } = data;
  const list = useMemo(
    () => ({
      name: data.listName || 'split-featured-key',
      id: data.contentId ? String(data.contentId) : undefined,
    }),
    [data.listName, data.contentId],
  );
  const markerRef = useRef<HTMLSpanElement>(null);
  const hostRef = useRef<HTMLElement | null>(null);
  const [ready, setReady] = useState(false);

  const item = useMemo(
    () => toGa4Item(product, list, 0),
    [product, list],
  );

  // Gate the list on `ready` so the observer effect re-runs after hostRef is set.
  const { onSelectItem } = useTrackItemList(
    hostRef,
    list,
    ready ? [item] : [],
  );

  useLayoutEffect(() => {
    hostRef.current =
      markerRef.current?.closest<HTMLElement>('.nfp14__featured') ?? null;
    setReady(true);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!ready || !host) {
      return;
    }
    const links = host.querySelectorAll<HTMLElement>(
      '.nfp14__featured-image-link, .nfp14__featured-title-link',
    );
    const handler = () => onSelectItem(item, 0);
    for (const link of links) {
      link.addEventListener('click', handler);
    }
    return () => {
      for (const link of links) {
        link.removeEventListener('click', handler);
      }
    };
  }, [ready, onSelectItem, item]);

  return <span ref={markerRef} style={{ display: 'none' }} aria-hidden="true" />;
};